export type SpatialLayoutMode = 'stack' | 'tile';

/** Front-first order. `order[0]` is the nearest panel. */
export function bringToFront(order: string[], id: string): string[] {
  if (!order.includes(id)) return order;
  return [id, ...order.filter((item) => item !== id)];
}

export function depthOf(order: string[], id: string): number {
  const index = order.indexOf(id);
  return index < 0 ? order.length : index;
}

export function moveBefore(order: string[], movingId: string, beforeId: string): string[] {
  if (movingId === beforeId || !order.includes(movingId) || !order.includes(beforeId)) return order;
  const without = order.filter((id) => id !== movingId);
  const index = without.indexOf(beforeId);
  return [...without.slice(0, index), movingId, ...without.slice(index)];
}

/** Keep a remembered order, drop closed ids, append newly opened ones. */
export function syncWindowOrder(order: string[], openIds: string[]): string[] {
  const open = new Set(openIds);
  const kept = order.filter((id) => open.has(id));
  const missing = openIds.filter((id) => !kept.includes(id));
  return [...kept, ...missing];
}

/** Highest urgency first. Equal urgency keeps the incoming order. */
export function orderByUrgency(panels: { id: string; urgency: number }[]): string[] {
  return panels
    .map((panel, index) => ({ ...panel, index }))
    .sort((a, b) => b.urgency - a.urgency || a.index - b.index)
    .map((panel) => panel.id);
}

/** Enough slots for every dock destination plus a few extras. */
export const SPATIAL_RECENT_CAP = 32;

export function rememberPath(recents: string[], path: string, max = SPATIAL_RECENT_CAP): string[] {
  if (!path) return recents;
  return [path, ...recents.filter((item) => item !== path)].slice(0, max);
}

export function forgetPath(recents: string[], path: string): string[] {
  if (!path) return recents;
  return recents.filter((item) => item !== path);
}

/** Rear windows from recents, or the desktop home so a deep link still looks like a stack. */
export function rearWindowPaths(
  pathname: string,
  recents: string[],
  fallback = '/',
  limit = Number.POSITIVE_INFINITY,
  closed: string[] = [],
): string[] {
  const closedSet = new Set(closed);
  const remembered = recents.filter((path) => path !== pathname && !closedSet.has(path));
  if (remembered.length > 0) return remembered.slice(0, limit);
  if (pathname !== fallback && !closedSet.has(fallback)) return [fallback];
  return [];
}
